import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventoryItems } from '../../hooks/useInventory';
import { DataTable } from '@/features/shared/components/data-display/DataTable';
import { Input } from '@tonic/shared/components/ui';
import { Button } from '@tonic/shared/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { PageTitle } from '@/components/ui/PageTitle';
import { columns } from './columns';
import { InventoryFilters } from './InventoryFilters';
import { Download, Filter, MoreHorizontal, Plus, Trash } from 'lucide-react';

/**
 * @typedef {Object} InventoryItem
 * @property {string} id - Unique identifier for the item
 * @property {string} name - Name of the item
 * @property {string} sku - Stock keeping unit
 * @property {number} quantity - Current quantity in stock
 * @property {string} status - Current status of the item
 */

/**
 * Inventory list component that displays and manages inventory items
 * @returns {JSX.Element} The inventory list component
 */
export function InventoryList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    locationId: null,
    status: null,
  });

  const { data: items, isLoading } = useInventoryItems({
    ...filters,
    search: searchQuery,
  });

  const handleFilterChange = newFilters => {
    setFilters(newFilters);
  };

  const actions = (
    <Button onClick={() => navigate('/inventory/add')}>
      <Plus className="mr-2 h-4 w-4" />
      Add Item
    </Button>
  );

  return (
    <div className="space-y-4">
      <PageTitle
        title="Inventory"
        description="Manage your inventory items across all locations."
        actions={actions}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search inventory..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-[300px]"
          />
          <InventoryFilters
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>
        <div className="flex items-center gap-2">
          {selectedItems.length > 0 && (
            <Button variant="outline" size="sm">
              <Trash className="mr-2 h-4 w-4" />
              Delete Selected
            </Button>
          )}
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Import Items</DropdownMenuItem>
              <DropdownMenuItem>Bulk Update</DropdownMenuItem>
              <DropdownMenuItem>Print Labels</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items || []}
        isLoading={isLoading}
        onRowSelectionChange={setSelectedItems}
      />
    </div>
  );
}

InventoryList.propTypes = {
  // No props needed for this component
};
